/**
 * SPD Data Service - ENHANCED WITH SEMANTIC SEARCH
 * Uses actual calculated values from SPD spreadsheet + semantic understanding
 */

import SemanticSearchService from './semanticSearchService';

class SPDDataService {
  static spdData = null;
  static projectEmbeddings = new Map(); // Cache for project embeddings
  static OPENAI_API_KEY = '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';
  
  /**
   * Load SPD data from corrected JSON file
   */
  static async loadSPDData() {
    if (this.spdData) return this.spdData;
    
    try {
      const response = await fetch('/spd_complete.json');
      if (!response.ok) {
        throw new Error(`Failed to load SPD data: ${response.status}`);
      }
      this.spdData = await response.json();
      return this.spdData;
    } catch (error) {
      console.error('Error loading SPD data:', error);
      // Fallback to engagement version if corrected version fails
      try {
        const response = await fetch('/spd_comprehensive.json');
        this.spdData = await response.json();
        return this.spdData;
      } catch (fallbackError) {
        console.error('Error loading fallback SPD data:', fallbackError);
        return { projects: [], metadata: { total_projects: 0 } };
      }
    }
  }

  /**
   * Get cost per square foot data by building type - DYNAMIC (no hardcoding)
   */
  static async getCostPerSF(buildingType = null) {
    const data = await this.loadSPDData();
    const projects = data.projects || [];
    
    // Calculate actual costs from real data
    const buildingTypeCosts = {};
    
    projects.forEach(project => {
      const useType = project['Use Type 1'] || project['Asset Category'] || 'Unknown';
      const city = project['City'] || '';
      const state = project['State'] || '';
      const location = `${city}, ${state}`.trim();
      
      // Get cost data (prefer actual, fallback to planned)
      const actualCostGSF = project['Actual Hard Cost / GSF'];
      const plannedCostGSF = project['Hard Cost / GSF'];
      const costGSF = (typeof actualCostGSF === 'number' && actualCostGSF > 0) ? actualCostGSF : plannedCostGSF;
      
      if (typeof costGSF === 'number' && costGSF > 0) {
        if (!buildingTypeCosts[useType]) {
          buildingTypeCosts[useType] = {
            type: useType,
            costs: [],
            projects: [],
            locations: new Set()
          };
        }
        
        buildingTypeCosts[useType].costs.push(costGSF);
        buildingTypeCosts[useType].projects.push({
          name: project['Project Name'],
          cost: costGSF,
          location: location,
          gsf: project['Project Scope (GSF)']
        });
        buildingTypeCosts[useType].locations.add(location);
      }
    });
    
    // Calculate statistics for each building type
    const buildingTypes = Object.values(buildingTypeCosts).map(bt => {
      const costs = bt.costs;
      if (costs.length === 0) return null;
      
      costs.sort((a, b) => a - b);
      const avg = costs.reduce((sum, cost) => sum + cost, 0) / costs.length;
      const median = costs[Math.floor(costs.length / 2)];
      const min = costs[0];
      const max = costs[costs.length - 1];
      
      return {
        type: bt.type,
        avgCostPerSF: Math.round(avg),
        medianCostPerSF: Math.round(median),
        rangeMin: Math.round(min),
        rangeMax: Math.round(max),
        projectCount: bt.projects.length,
        locations: Array.from(bt.locations),
        description: `${bt.projects.length} projects across ${bt.locations.size} locations`,
        sampleProjects: bt.projects.slice(0, 3)
      };
    }).filter(bt => bt !== null);
    
    if (buildingType) {
      return buildingTypes.find(bt => 
        bt.type.toLowerCase().includes(buildingType.toLowerCase())
      ) || null;
    }
    
    return buildingTypes;
  }

  /**
   * Get construction cost index trends - DYNAMIC from real data
   */
  static async getConstructionIndex(quarters = 6) {
    const data = await this.loadSPDData();
    const projects = data.projects || [];
    
    // Calculate index trends from project start dates and costs
    const indexByQuarter = {};
    
    projects.forEach(project => {
      const startDate = project['SPD Start Date'] || project['Actual Construction Start'];
      const costIndex = project['Cost Index at Start'];
      
      if (startDate && typeof costIndex === 'number' && costIndex > 0) {
        const date = new Date(startDate);
        const year = date.getFullYear();
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        const quarterKey = `${year}-Q${quarter}`;
        
        if (!indexByQuarter[quarterKey]) {
          indexByQuarter[quarterKey] = {
            date: quarterKey,
            indices: [],
            projects: 0
          };
        }
        
        indexByQuarter[quarterKey].indices.push(costIndex);
        indexByQuarter[quarterKey].projects++;
      }
    });
    
    // Calculate average index per quarter
    const indexTrends = Object.values(indexByQuarter)
      .map(quarter => ({
        date: quarter.date,
        index: quarter.indices.reduce((sum, idx) => sum + idx, 0) / quarter.indices.length,
        projects: quarter.projects,
        annualChange: 0 // Will be calculated below
      }))
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, quarters);
    
    // Calculate annual changes
    indexTrends.forEach((current, i) => {
      const yearAgo = indexTrends.find(trend => {
        const currentYear = parseInt(current.date.split('-')[0]);
        const trendYear = parseInt(trend.date.split('-')[0]);
        return trendYear === currentYear - 1;
      });
      
      if (yearAgo) {
        current.annualChange = ((current.index - yearAgo.index) / yearAgo.index) * 100;
      }
    });
    
    return indexTrends;
  }

  /**
   * INTELLIGENT search for projects - SEMANTIC SEARCH + SMART FILTERING + AI
   */
  static async searchData(query) {
    const data = await this.loadSPDData();
    const projects = data.projects || [];
    
    if (!query) {
      return {
        projects: projects.slice(0, 100),
        buildingTypes: await this.getCostPerSF(),
        marketData: await this.getMarketAnalysis(),
        rawProjects: projects
      };
    }
    
    console.log('🔍 SEMANTIC SEARCH: Processing query:', query);
    
    // STEP 1: Expand query with construction ontology
    const expansion = SemanticSearchService.expandQueryTerms(query);
    console.log('🧠 Query expanded with', expansion.expandedTerms.length, 'terms');
    
    // STEP 2: Generate embedding for query
    const queryEmbedding = await SemanticSearchService.getEmbedding(query, this.OPENAI_API_KEY);
    
    // STEP 3: SMART QUERY PARSING - Extract location, building type, etc.
    const queryFilters = this.parseQuery(query);
    console.log('Parsed query filters:', queryFilters);
    
    // INTELLIGENT PROJECT FILTERING
    let filteredProjects = projects.filter(project => {
      let matches = true;
      
      // Location filtering with intelligent matching
      if (queryFilters.locations.length > 0) {
        const projectLocation = this.normalizeLocation(project);
        matches = matches && queryFilters.locations.some(loc => 
          this.locationMatches(projectLocation, loc)
        );
      }
      
      // Building type filtering with category intelligence
      if (queryFilters.buildingTypes.length > 0) {
        const projectType = this.normalizeBuildingType(project);
        matches = matches && queryFilters.buildingTypes.some(type => 
          this.buildingTypeMatches(projectType, type)
        );
      }
      
      // Cost range filtering
      if (queryFilters.costRange.min || queryFilters.costRange.max) {
        const costPerSF = project['Hard Cost / GSF'] || project['Actual Hard Cost / GSF'];
        if (typeof costPerSF === 'number' && costPerSF > 0) {
          if (queryFilters.costRange.min && costPerSF < queryFilters.costRange.min) matches = false;
          if (queryFilters.costRange.max && costPerSF > queryFilters.costRange.max) matches = false;
        }
      }
      
      // Year filtering for start dates
      if (queryFilters.years.length > 0) {
        const projectYear = this.extractProjectYear(project);
        if (projectYear) {
          matches = matches && queryFilters.years.includes(projectYear);
        }
      }
      
      return matches;
    });
    
    // STEP 4: Apply concept-based filters from ontology
    filteredProjects = SemanticSearchService.applyConceptFilters(filteredProjects, expansion.concepts);
    
    console.log(`Filtered from ${projects.length} to ${filteredProjects.length} projects`);
    
    // STEP 5: Calculate semantic similarity scores if embedding available
    let scoredProjects = filteredProjects;
    if (queryEmbedding) {
      console.log('🎯 Calculating semantic similarity scores...');
      
      // Generate or retrieve project embeddings
      const projectsWithScores = await Promise.all(
        filteredProjects.map(async (project) => {
          const projectText = SemanticSearchService.createProjectSearchText(project);
          const projectId = project['Project ID'] || project['Project Name'];
          
          // Get or generate project embedding
          let projectEmbedding = this.projectEmbeddings.get(projectId);
          if (!projectEmbedding) {
            projectEmbedding = await SemanticSearchService.getEmbedding(projectText, this.OPENAI_API_KEY);
            if (projectEmbedding) {
              this.projectEmbeddings.set(projectId, projectEmbedding);
            }
          }
          
          // Calculate similarity
          const similarity = projectEmbedding 
            ? SemanticSearchService.cosineSimilarity(queryEmbedding, projectEmbedding)
            : 0;
          
          return {
            ...project,
            semanticScore: similarity,
            projectText: projectText
          };
        })
      );
      
      // Sort by semantic similarity (highest first)
      scoredProjects = projectsWithScores.sort((a, b) => b.semanticScore - a.semanticScore);
      console.log('🎯 Top match semantic score:', scoredProjects[0]?.semanticScore);
    }
    
    // STEP 6: Enrich filtered projects for AI analysis
    const enrichedProjects = scoredProjects.map(project => {
      return {
        ...project,
        searchableText: [
          project['Project Name'],
          project['Primary Client'],
          project['City'],
          project['State'],
          project['Project Street Address'],
          project['Use Type 1'],
          project['Asset Category'],
          project['Development Type'],
          project['Developer Sponsor 1'],
          project['Construction Manager 1'],
          project['Design Architect']
        ].filter(Boolean).join(' '),
        
        costPerSF: project['Hard Cost / GSF'] || project['Actual Hard Cost / GSF'],
        totalCost: project['Planned Hard & Soft Costs'] || project['Actual Hard & Soft Costs'],
        gsf: project['Project Scope (GSF)'],
        
        // ALL cost fields for comprehensive analysis
        costs: {
          hardCostPerGSF: project['Hard Cost / GSF'],
          actualHardCostPerGSF: project['Actual Hard Cost / GSF'],
          plannedHardCosts: project['Planned Hard Costs'],
          actualHardCosts: project['Actual Hard Costs'],
          plannedHardSoftCosts: project['Planned Hard & Soft Costs'],
          actualHardSoftCosts: project['Actual Hard & Soft Costs'],
          totalProjectCost: project['Total Project Cost'],
          constructionCost: project['Construction Cost'],
          softCosts: project['Soft Costs'],
          contingency: project['Contingency'],
          escalation: project['Escalation'],
          otherCosts: project['Other Costs'],
          budgetVariance: project['Budget Variance'],
          costIndex: project['Cost Index at Start']
        },
        
        dates: {
          spdStart: project['SPD Start Date'],
          plannedStart: project['Planned Construction Start Date'],
          plannedCompletion: project['Planned Substantial Completion Date'],
          actualStart: project['Actual Construction Start'],
          actualCompletion: project['Actual Substantial Completion Date']
        }
      };
    });
    
    return {
      projects: enrichedProjects.slice(0, 50), // Send most relevant subset to AI
      buildingTypes: await this.getCostPerSF(),
      marketData: await this.getMarketAnalysis(query),
      rawProjects: enrichedProjects, // All filtered results
      totalCount: enrichedProjects.length,
      queryFilters: queryFilters // Pass filters to AI for context
    };
  }

  /**
   * SMART QUERY PARSING - Extract location, building types, cost ranges
   */
  static parseQuery(query) {
    const queryLower = query.toLowerCase();
    
    // 🔥 STRICT Location extraction - extract individual words, match specifically
    const locations = [];
    const queryWords = queryLower.split(/\s+/);
    
    const locationMap = {
      'manhattan': 'manhattan',
      'brooklyn': 'brooklyn',
      'queens': 'queens',
      'bronx': 'bronx',
      'nyc': 'new york',
      'new york': 'new york',
      'miami': 'miami',
      'chicago': 'chicago',
      'la': 'los angeles',
      'los angeles': 'los angeles',
      'sf': 'san francisco',
      'san francisco': 'san francisco',
      'boston': 'boston',
      'seattle': 'seattle',
      'denver': 'denver',
      'austin': 'austin',
      'atlanta': 'atlanta',
      'dc': 'washington',
      'washington': 'washington',
      'philly': 'philadelphia',
      'philadelphia': 'philadelphia',
      'dallas': 'dallas',
      'houston': 'houston'
    };
    
    // Extract locations from individual words (not patterns!)
    queryWords.forEach(word => {
      if (locationMap[word]) {
        if (!locations.includes(locationMap[word])) {
          locations.push(locationMap[word]);
        }
      }
    });
    
    // Handle multi-word locations like "new york", "los angeles", "san francisco"
    if (queryLower.includes('new york')) {
      if (!locations.includes('new york')) locations.push('new york');
    }
    if (queryLower.includes('los angeles')) {
      if (!locations.includes('los angeles')) locations.push('los angeles');
    }
    if (queryLower.includes('san francisco')) {
      if (!locations.includes('san francisco')) locations.push('san francisco');
    }
    
    // Building type extraction with category intelligence
    const buildingTypes = [];
    const typePatterns = {
      'residential': ['condo', 'condominium', 'apartment', 'residential', 'multifamily', 'housing', 'tower', 'high-rise', 'luxury'],
      'office': ['office', 'commercial', 'corporate', 'workplace', 'business'],
      'retail': ['retail', 'shopping', 'store', 'mall', 'plaza'],
      'hotel': ['hotel', 'hospitality', 'resort', 'inn'],
      'mixed-use': ['mixed-use', 'mixed use', 'mixed'],
      'industrial': ['industrial', 'warehouse', 'manufacturing'],
      'healthcare': ['hospital', 'medical', 'healthcare', 'clinic'],
      'education': ['school', 'university', 'college', 'education'],
      'cultural': ['museum', 'theater', 'cultural', 'art']
    };
    
    for (const [type, keywords] of Object.entries(typePatterns)) {
      if (keywords.some(keyword => queryLower.includes(keyword))) {
        buildingTypes.push(type);
      }
    }
    
    // Cost range extraction
    const costRange = { min: null, max: null };
    const costMatches = query.match(/\$(\d+(?:,\d{3})*(?:\.\d{2})?)/g);
    if (costMatches && costMatches.length >= 1) {
      const costs = costMatches.map(m => parseInt(m.replace(/[$,]/g, '')));
      if (costs.length === 1) {
        costRange.max = costs[0];
      } else {
        costRange.min = Math.min(...costs);
        costRange.max = Math.max(...costs);
      }
    }
    
    // Year extraction for date filtering
    const years = [];
    const yearMatches = query.match(/\b(20\d{2})\b/g);
    if (yearMatches) {
      years.push(...yearMatches.map(y => parseInt(y)));
    }
    
    return {
      locations,
      buildingTypes,
      costRange,
      years,
      originalQuery: query
    };
  }

  /**
   * LOCATION NORMALIZATION AND MATCHING
   */
  static normalizeLocation(project) {
    const city = (project['City'] || '').toLowerCase().trim();
    const state = (project['State'] || '').toLowerCase().trim();
    const address = (project['Project Street Address'] || '').toLowerCase().trim();
    
    return {
      city,
      state,
      address,
      fullLocation: `${city}, ${state}`.trim()
    };
  }
  
  static locationMatches(projectLocation, queryLocation) {
    const queryLower = queryLocation.toLowerCase();
    
    // Direct city match
    if (projectLocation.city.includes(queryLower)) return true;
    
    // State abbreviation handling
    const stateMap = {
      'ny': 'new york',
      'ca': 'california',
      'fl': 'florida',
      'tx': 'texas',
      'il': 'illinois',
      'wa': 'washington'
    };
    
    if (stateMap[projectLocation.state] && stateMap[projectLocation.state].includes(queryLower)) return true;
    
    // Special location mappings
    if (queryLocation === 'manhattan' && 
        (projectLocation.city.includes('new york') || 
         projectLocation.city.includes('manhattan') ||
         projectLocation.address.includes('manhattan'))) return true;
         
    if (queryLocation === 'miami' && 
        (projectLocation.city.includes('miami') ||
         projectLocation.city.includes('south beach'))) return true;
    
    return false;
  }

  /**
   * BUILDING TYPE NORMALIZATION AND MATCHING
   */
  static normalizeBuildingType(project) {
    const useType = (project['Use Type 1'] || '').toLowerCase();
    const category = (project['Asset Category'] || '').toLowerCase();
    const devType = (project['Development Type'] || '').toLowerCase();
    
    return {
      useType,
      category,
      devType,
      combined: `${useType} ${category} ${devType}`.trim()
    };
  }
  
  static buildingTypeMatches(projectType, queryType) {
    const queryLower = queryType.toLowerCase();
    
    // Direct matches
    if (projectType.combined.includes(queryLower)) return true;
    
    // Category intelligence mappings
    const categoryMappings = {
      'residential': ['residential', 'multifamily', 'condo', 'apartment', 'housing', 'luxury residential'],
      'office': ['office', 'commercial', 'corporate'],
      'retail': ['retail', 'shopping'],
      'hotel': ['hotel', 'hospitality'],
      'mixed-use': ['mixed', 'mixed-use'],
      'industrial': ['industrial', 'warehouse'],
      'healthcare': ['healthcare', 'medical', 'hospital'],
      'education': ['education', 'school', 'university']
    };
    
    if (categoryMappings[queryType]) {
      return categoryMappings[queryType].some(cat => 
        projectType.combined.includes(cat)
      );
    }
    
    return false;
  }

  /**
   * EXTRACT PROJECT YEAR from various date fields
   */
  static extractProjectYear(project) {
    // Try different date fields in order of preference
    const dateFields = [
      project['Actual Construction Start'],
      project['Planned Construction Start Date'], 
      project['SPD Start Date'],
      project['Actual Substantial Completion Date'],
      project['Planned Substantial Completion Date']
    ];
    
    for (const dateField of dateFields) {
      if (dateField) {
        const date = new Date(dateField);
        if (!isNaN(date.getTime())) {
          return date.getFullYear();
        }
      }
    }
    
    return null;
  }

  /**
   * REMOVED - Dynamic location intelligence now handled by AI
   * No hardcoded patterns - let GPT understand geography and context
   */

  /**
   * REMOVED - Dynamic building type intelligence now handled by AI
   * No hardcoded patterns - let GPT understand building types and categories
   */

  /**
   * Get market analysis - DYNAMIC calculations
   */
  static async getMarketAnalysis(filter = null) {
    const data = await this.loadSPDData();
    const projects = data.projects || [];
    
    let filteredProjects = projects;
    
    if (filter) {
      const filterLower = filter.toLowerCase();
      filteredProjects = projects.filter(project => {
        const searchFields = [
          project['City'],
          project['State'],
          project['Use Type 1'],
          project['Asset Category'],
          project['Development Type']
        ];
        
        return searchFields.some(field => 
          field && field.toString().toLowerCase().includes(filterLower)
        );
      });
    }
    
    if (filteredProjects.length === 0) {
      return null;
    }
    
    // Calculate market metrics
    let totalGSF = 0;
    let totalCosts = 0;
    let validCostProjects = 0;
    const costs = [];
    
    filteredProjects.forEach(project => {
      const gsf = project['Project Scope (GSF)'];
      const actualCosts = project['Actual Hard Costs'] || project['Planned Hard Costs'];
      const actualCostGSF = project['Actual Hard Cost / GSF'];
      const plannedCostGSF = project['Hard Cost / GSF'];
      const costGSF = (typeof actualCostGSF === 'number' && actualCostGSF > 0) ? actualCostGSF : plannedCostGSF;
      
      if (typeof gsf === 'number' && gsf > 0) {
        totalGSF += gsf;
      }
      
      if (typeof actualCosts === 'number' && actualCosts > 0) {
        totalCosts += actualCosts;
      }
      
      if (typeof costGSF === 'number' && costGSF > 0) {
        costs.push(costGSF);
        validCostProjects++;
      }
    });
    
    const avgCostPerSF = costs.length > 0 ? costs.reduce((sum, cost) => sum + cost, 0) / costs.length : 0;
    
    return {
      buildingType: filter || 'All Types',
      projectCount: filteredProjects.length,
      validCostProjects: validCostProjects,
      totalSquareFeet: totalGSF.toLocaleString(),
      totalHardCosts: totalCosts.toLocaleString(),
      avgCostPerSF: Math.round(avgCostPerSF),
      marketTrend: '+3.4', // Could be calculated from index trends
      costRange: costs.length > 0 ? `$${Math.round(Math.min(...costs))}-${Math.round(Math.max(...costs))}/SF` : null
    };
  }

  /**
   * Get summary statistics - DYNAMIC
   */
  static async getSummaryStats() {
    const data = await this.loadSPDData();
    const projects = data.projects || [];
    
    const cities = new Set();
    const states = new Set();
    const buildingTypes = new Set();
    const specialties = new Set();
    let activeProjects = 0;
    let totalGSF = 0;
    
    projects.forEach(project => {
      if (project['City']) cities.add(project['City']);
      if (project['State']) states.add(project['State']);
      if (project['Use Type 1']) buildingTypes.add(project['Use Type 1']);
      if (project['Asset Category']) specialties.add(project['Asset Category']);
      if (project['Status'] === 'Active') activeProjects++;
      
      const gsf = project['Project Scope (GSF)'];
      if (typeof gsf === 'number' && gsf > 0) {
        totalGSF += gsf;
      }
    });
    
    return {
      totalProjects: projects.length,
      activeProjects: activeProjects,
      buildingTypes: buildingTypes.size,
      specialties: Array.from(specialties).slice(0, 10),
      totalSquareFeet: totalGSF.toLocaleString(),
      coverageAreas: Array.from(cities).slice(0, 15),
      dataLastUpdated: data.metadata?.extracted_date || 'Recent',
      dataSource: 'SPD Database (Calculated Values)'
    };
  }
}

export default SPDDataService;
